import { useRef } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { PublicDevNav } from '@/components/PublicDevNav';
import { Button } from '@ludolio/shared-ui';
import { ArrowRight, BookOpen, Mail } from 'lucide-react';

export const Route = createFileRoute('/')({
  component: IndexPage,
});

const HERO_BASE_TRANSFORM = 'rotateX(8deg) rotateY(-14deg) rotateZ(2deg)';

function HeroMockup() {
  const frameRef = useRef<HTMLDivElement>(null);
  const isDarkMode = useThemeStore((state) => state.isDarkMode);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = frameRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `rotateX(${8 - py * 18}deg) rotateY(${-14 + px * 24}deg) rotateZ(2deg)`;
  };

  const handleMouseLeave = () => {
    const el = frameRef.current;
    if (el) el.style.transform = HERO_BASE_TRANSFORM;
  };

  return (
    <div
      aria-hidden="true"
      className="relative mx-auto mt-12 w-full max-w-md lg:absolute lg:right-0 lg:top-1/2 lg:mt-0 lg:w-[36rem] lg:max-w-none lg:-translate-y-1/2 [perspective:1600px]"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="animate-dev-float motion-reduce:animate-none">
        <div
          ref={frameRef}
          style={{ transform: HERO_BASE_TRANSFORM }}
          className="overflow-hidden rounded-2xl border border-[hsl(var(--color-border))]/15 bg-[hsl(var(--color-card))]/95 shadow-[0_45px_90px_-20px_rgba(0,0,0,0.4),0_0_80px_hsla(21,80%,48%,0.14)] transition-transform duration-200 ease-out will-change-transform motion-reduce:transition-none dark:shadow-[0_45px_90px_-20px_rgba(0,0,0,0.7),0_0_80px_hsla(21,80%,48%,0.14)]"
        >
          <div className="flex items-center gap-1.5 border-b border-[hsl(var(--color-border))]/10 bg-[hsl(var(--color-muted))] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-3 h-5 flex-1 rounded-md bg-[hsl(var(--color-foreground))]/[0.06]" />
          </div>
          <img
            src={isDarkMode ? '/portal-dark.png' : '/portal-light.png'}
            alt=""
            className="block aspect-[16/10] w-full bg-[hsl(var(--color-muted))] object-cover object-top"
          />
        </div>
      </div>
    </div>
  );
}

function IndexPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <div className="min-h-screen bg-[hsl(var(--color-background))] text-[hsl(var(--color-foreground))] selection:bg-[hsl(var(--color-primary))]/35">
      <PublicDevNav isAuthenticated={isAuthenticated} />

      <main id="main-content">
        <section id="overview" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_24%_20%,hsla(21,80%,48%,0.12),transparent_28%),radial-gradient(circle_at_78%_18%,rgba(244,158,78,0.08),transparent_27%),linear-gradient(180deg,hsl(var(--color-background))_0%,hsl(var(--color-background))_100%)] dark:bg-[radial-gradient(circle_at_24%_20%,hsla(21,80%,48%,0.34),transparent_28%),radial-gradient(circle_at_78%_18%,rgba(244,158,78,0.18),transparent_27%),linear-gradient(180deg,#1b120e_0%,#17100d_58%,#160f0b_100%)]" />
          <div className="animate-dev-drift-slow motion-reduce:animate-none absolute left-[18%] top-24 h-72 w-[38rem] rounded-full bg-[hsla(21,80%,48%,0.08)] blur-3xl dark:bg-[hsla(21,80%,48%,0.18)]" />
          <div className="animate-dev-drift-fast motion-reduce:animate-none absolute right-[-10rem] top-28 h-96 w-96 rounded-full bg-[hsla(21,80%,48%,0.06)] blur-3xl dark:bg-[hsla(21,80%,48%,0.1)]" />

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
            <div className="flex max-w-3xl flex-col justify-center lg:pt-16">
              <h1 className="max-w-xl text-3xl font-black leading-[1.4] tracking-[-0.025em] sm:text-5xl lg:text-6xl">
                How to distribute your game with Ludolio
              </h1>
              <div className="mt-9 flex flex-col gap-3">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg">
                    <Link to={isAuthenticated ? '/dashboard' : '/signup'}>
                      Create your account <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-[hsl(var(--color-primary))]/28 bg-[hsl(var(--color-card))]/50 text-[hsl(var(--color-foreground))] hover:bg-[hsl(var(--color-primary))]/12 hover:text-[hsl(var(--color-foreground))]">
                    <Link to="/guide">
                      Find out more <BookOpen className="h-4 w-4" />
                    </Link>
                  </Button>
                </div> 
                <Button asChild size="lg" variant="outline" className="border-[hsl(var(--color-primary))]/28 bg-[hsl(var(--color-card))]/50 text-[hsl(var(--color-foreground))] hover:bg-[hsl(var(--color-primary))]/12 hover:text-[hsl(var(--color-foreground))] sm:self-start">
                  <a href="mailto:ben@ludolio.com?subject=Game%20distribution%20inquiry">
                    Contact us <Mail className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <HeroMockup />
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6 lg:px-8 lg:pb-28">
          <div className="mb-7 max-w-xl">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[hsl(var(--color-primary-text))]">Setup</p>
              <h2 className="mt-3 text-3xl font-black tracking-[-0.01em] sm:text-4xl">What you will need</h2>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { step: '01', title: 'Your game details' },
                { step: '02', title: 'Builds for each platform' },
                { step: '03', title: 'Ludolio integrations' },
                { step: '04', title: 'Quick test' },
              ].map((item) => (
                <article key={item.title} className="flex items-center rounded-2xl border border-[hsl(var(--color-border))] bg-[hsl(var(--color-card))]/70 p-5 transition hover:border-[hsl(var(--color-primary))]/30 hover:bg-[hsl(var(--color-card))]/90">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-black tracking-[0.12em] text-[hsl(var(--color-primary-text))]">{item.step}</span>
                    <h3 className="text-lg font-black tracking-[-0.005em]">{item.title}</h3>
                  </div>
                </article>
              ))}
            </div>
            <div className="flex flex-col justify-between rounded-2xl border border-[hsl(var(--color-primary))]/20 bg-[hsl(var(--color-primary))]/9 p-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[hsl(var(--color-primary-text))]">Next</p>
                <h3 className="mt-3 text-2xl font-black tracking-[-0.01em]">Read our getting started guide</h3>
              </div>
              <Button asChild className="mt-8 w-fit">
                <Link to="/guide">Read guide</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
          <figure className="relative overflow-hidden rounded-[2rem] border border-[hsl(var(--color-border))] shadow-2xl dark:shadow-black/30">
            <img
              src="/wayward-strand.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-[hsl(var(--color-background))]/82 dark:bg-[rgba(22,15,11,0.82)] sm:hidden" />
            <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,hsl(var(--color-background))_0%,hsl(var(--color-background))_40%,transparent_100%)] dark:bg-[linear-gradient(90deg,#160f0b_0%,rgba(22,15,11,0.92)_38%,rgba(22,15,11,0.55)_70%,rgba(22,15,11,0.25)_100%)] sm:block" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,hsla(21,80%,48%,0.18),transparent_36%)]" />
            <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-6 select-none font-serif text-[11rem] leading-none text-[hsl(var(--color-primary))]/25 sm:text-[14rem]">
              &ldquo;
            </span>
            <div className="relative max-w-3xl p-8 sm:p-12 lg:p-16">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[hsl(var(--color-primary-text))]">From developers</p>
              <blockquote className="mt-6 text-2xl font-bold italic leading-snug tracking-[-0.01em] sm:text-3xl lg:text-4xl">
                &ldquo;The SDK was uncomplicated and intuitive, and it was easy to connect to our existing infrastructure for platform implementations.&rdquo;
              </blockquote>
              <figcaption className="mt-9 flex items-center gap-4">
                <div>
                  <p className="font-black">Russell Dilley</p>
                  <p className="mt-0.5 text-sm text-[hsl(var(--color-muted-foreground))]">Wayward Strand</p>
                </div>
              </figcaption>
            </div>
          </figure>
        </section>
      </main>
    </div>
  );
}

